import { generationStepSubtitle } from '@/features/datasets/utils/generationPhaseLabels';

export const LABEL_GEN_WAIT_FOR_MODEL_CONFIG =
    "Starts after you add your prompts and finish the 'Model configuration' step";

export const LABEL_GEN_WAIT_FOR_PROCESSING =
    'Waiting for dataset processing to finish, then labels will generate';

/** Agentic TRAJECTORY analogues (same "Generating labels" step slot). */
export const TRAJECTORY_GEN_WAIT_FOR_MODEL_CONFIG =
    "Starts after you finish the 'Model configuration' step";

export const TRAJECTORY_GEN_WAIT_FOR_PROCESSING =
    'Waiting for dataset processing to finish, then trajectories will generate';

export const GENERATION_STEP_LABEL_LABELS = 'Generating labels';
export const GENERATION_STEP_LABEL_TRAJECTORIES = 'Generating trajectories';

/** Step-row label for the post-processing generation step of a card. */
export function generationStepLabel(file) {
    return file?.isTrajectoryGeneration
        ? GENERATION_STEP_LABEL_TRAJECTORIES
        : GENERATION_STEP_LABEL_LABELS;
}

function waitingCopyFor(file, labelGenerationArmed) {
    if (file?.isTrajectoryGeneration) {
        return labelGenerationArmed
            ? TRAJECTORY_GEN_WAIT_FOR_PROCESSING
            : TRAJECTORY_GEN_WAIT_FOR_MODEL_CONFIG;
    }
    return labelGenerationArmed ? LABEL_GEN_WAIT_FOR_PROCESSING : LABEL_GEN_WAIT_FOR_MODEL_CONFIG;
}

/** True while Generate with AI (or label-only on the same card) is still running. */
export function isActiveGenerationCard(file) {
    return Boolean(
        file?.isGeneration && !file.isError && !file.isCompleted && !file.isWarning
    );
}

/**
 * Subtitle for the running label-generation row — the human phase label and
 * ``processed / remaining`` counts straight from the job (no estimates). Shown
 * under "Generating examples" on a Generate-with-AI card and under
 * "Generating labels" on an upload card; null whenever that row is not running.
 */
function resolveGenerationSubtitle(file, { uploadState, labelGenState }) {
    const rowIsRunning = file.isGeneration ? uploadState === 'active' : labelGenState === 'active';
    if (!rowIsRunning) return null;
    return generationStepSubtitle({
        jobPhase: file.jobPhase,
        processedPrompts: file.processedPrompts,
        totalPrompts: file.totalPrompts,
    });
}

export const INGEST_PHASE_ANALYZING = 'analyzing_file';
export const INGEST_PHASE_IMPORTING = 'importing_rows';

/**
 * A step percentage that never reads 0% once work has started: one decimal
 * below 10% (``"0.4%"``), ``"<0.1%"`` for the first sliver, and floored so a
 * running step never shows 100%.
 */
export function formatStagePct(pct) {
    const value = Math.max(0, Math.min(100, Number(pct) || 0));
    if (value >= 100) return '100%';
    if (value === 0) return '0%';
    if (value < 0.1) return '<0.1%';
    if (value < 10) return `${(Math.floor(value * 10) / 10).toFixed(1)}%`;
    return `${Math.floor(value)}%`;
}

/** ``"12,000 rows read"``, the only progress shown while the file total is unknown. */
export function formatRowsRead(processedRows) {
    return Number.isFinite(processedRows) ? `${processedRows.toLocaleString('en-US')} rows read` : null;
}

const NO_INGEST_STEPS = Object.freeze({
    analyzeState: null,
    analyzeRight: null,
    analyzeSubtitle: null,
    processingPctLabel: null,
});

/**
 * Split the server-side "processing" step of an upload card into
 * "Analyzing file" (the metadata pass over every row) and "Importing rows"
 * (records stored), each with its own percentage, so a large file never sits
 * on one step at 0% while work is happening.
 */
function splitIngestSteps(file, states) {
    const combined = states.processingState;
    const { ingestPhase, processedRows, totalRows } = file;
    const rawPct = typeof file.processingProgress === 'number' ? file.processingProgress : file.progress;
    const importPct = typeof rawPct === 'number' ? rawPct : 0;
    const analysisDone =
        combined === 'complete' || ingestPhase === INGEST_PHASE_IMPORTING || (!ingestPhase && importPct > 0);
    const analyzeState = analysisDone ? 'complete' : combined;
    const processingState = analysisDone ? combined : 'pending';

    const analyzing = analyzeState === 'active' && ingestPhase === INGEST_PHASE_ANALYZING;
    let analyzeRight = null;
    if (analyzeState === 'active') {
        if (!analyzing || !Number.isFinite(processedRows)) analyzeRight = 'Starting';
        else if (totalRows > 0) analyzeRight = formatStagePct((processedRows / totalRows) * 100);
        else analyzeRight = 'In progress';
    }

    return {
        processingState,
        analyzeState,
        analyzeRight,
        analyzeSubtitle: analyzing && !(totalRows > 0) ? formatRowsRead(processedRows) : null,
        processingPctLabel: processingState === 'active' ? formatStagePct(importPct) : null,
    };
}

/**
 * Map a two-phase progress card to upload / analyze / import / label-generation step states.
 * Right-slot rendering stays in FloatingProgressBar.
 */
export function deriveDatasetStepStates(file) {
    const states = deriveStepStates(file);
    return {
        ...states,
        ...(file.isGeneration ? NO_INGEST_STEPS : splitIngestSteps(file, states)),
        generationSubtitle: resolveGenerationSubtitle(file, states),
    };
}

function deriveStepStates(file) {
    const {
        isError,
        isCompleted,
        phase,
        uploadProgress: upRaw,
        processingProgress: procRaw,
        progress,
        isLabelGeneration,
        labelGenerationProgress,
        deferLabelGeneration,
        labelGenerationArmed,
    } = file;

    const uploadProgress = typeof upRaw === 'number' ? upRaw : null;
    const processingProgress = typeof procRaw === 'number' ? procRaw : 0;
    const overall = typeof progress === 'number' ? progress : 0;

    const hasClientTwoPhase =
        uploadProgress !== null ||
        phase === 'uploading' ||
        phase === 'processing' ||
        phase === 'checking_labels' ||
        phase === 'label_generation' ||
        phase === 'completed';

    if (!hasClientTwoPhase) {
        const procComplete = overall >= 100 && !isError;
        const procActive = !isError && overall < 100 && (overall > 0 || Boolean(file.ingestPhase));
        const procErr = isError;
        return {
            understandState: null,
            understandPctDisplay: 0,
            uploadState: 'complete',
            processingState: procErr ? 'error' : procComplete ? 'complete' : procActive ? 'active' : 'pending',
            uploadPctDisplay: 100,
            processingPctDisplay: procActive ? Math.round(overall) : procComplete ? 100 : 0,
            labelGenState: isLabelGeneration ? 'pending' : null,
            labelGenPct: null,
            waitingCopy: null,
        };
    }

    const isInLabelGenPhase = phase === 'label_generation';

    if (file.isGeneration) {
        const isCheckingLabels =
            phase === 'checking_labels' || file.generationPhase === 'checking_labels';
        const isUnderstanding =
            phase === 'understanding_the_task' || file.generationPhase === 'understanding_the_task';
        const understandRaw = typeof file.understandProgress === 'number' ? file.understandProgress : overall;
        const understandPct = Math.max(0, Math.min(100, Math.floor(Number(understandRaw) || 0)));
        const genPct = Math.max(
            0,
            Math.min(100, Math.floor(Number(uploadProgress !== null ? uploadProgress : overall) || 0))
        );
        const checkPct = Math.max(
            0,
            Math.min(100, Math.floor(Number(processingProgress) || 0))
        );
        const labelGenPct = typeof labelGenerationProgress === 'number' ? labelGenerationProgress : 0;
        if (isError) {
            return {
                understandState: isUnderstanding ? 'error' : 'complete',
                understandPctDisplay: isUnderstanding ? understandPct : 100,
                uploadState: isUnderstanding ? 'pending' : genPct >= 100 || isCheckingLabels ? 'complete' : 'error',
                processingState: isCheckingLabels || (!isUnderstanding && genPct >= 100) ? 'error' : 'pending',
                uploadPctDisplay: isUnderstanding ? 0 : isCheckingLabels ? 100 : genPct,
                processingPctDisplay: isCheckingLabels ? checkPct : 0,
                labelGenState: isLabelGeneration ? 'error' : null,
                labelGenPct,
                waitingCopy: null,
            };
        }
        if (isCompleted || phase === 'completed') {
            return {
                understandState: 'complete',
                understandPctDisplay: 100,
                uploadState: 'complete',
                processingState: 'complete',
                uploadPctDisplay: 100,
                processingPctDisplay: 100,
                labelGenState: isLabelGeneration ? (file.isWarning ? 'warning' : 'complete') : null,
                labelGenPct,
                waitingCopy: null,
            };
        }
        if (isUnderstanding) {
            return {
                understandState: 'active',
                understandPctDisplay: understandPct,
                uploadState: 'pending',
                processingState: 'pending',
                uploadPctDisplay: 0,
                processingPctDisplay: 0,
                labelGenState: null,
                labelGenPct,
                waitingCopy: null,
            };
        }
        if (isCheckingLabels) {
            return {
                understandState: 'complete',
                understandPctDisplay: 100,
                uploadState: 'complete',
                processingState: 'active',
                uploadPctDisplay: 100,
                processingPctDisplay: checkPct,
                labelGenState: null,
                labelGenPct,
                waitingCopy: null,
            };
        }
        return {
            understandState: 'complete',
            understandPctDisplay: 100,
            uploadState: genPct >= 100 ? 'complete' : 'active',
            processingState: 'pending',
            uploadPctDisplay: genPct,
            processingPctDisplay: 0,
            labelGenState: null,
            labelGenPct,
            waitingCopy: null,
        };
    }

    const uploadFinished =
        uploadProgress !== null
            ? uploadProgress >= 100 || phase === 'processing' || phase === 'completed' || isInLabelGenPhase
            : phase === 'processing' || phase === 'completed' || isInLabelGenPhase;

    const uploadRunning = !isError && !uploadFinished && phase === 'uploading';

    let uploadState = 'pending';
    if (uploadFinished) uploadState = 'complete';
    else if (isError) uploadState = 'error';
    else if (uploadRunning) uploadState = 'active';

    const processingFinished =
        uploadFinished &&
        (isInLabelGenPhase || isCompleted || processingProgress >= 100 || phase === 'completed');

    const processingRunning =
        !isError &&
        uploadFinished &&
        !processingFinished &&
        (phase === 'processing' || processingProgress > 0 || overall >= 50);

    let processingState = 'pending';
    if (processingFinished) processingState = 'complete';
    else if (isError && uploadFinished && !isInLabelGenPhase) processingState = 'error';
    else if (processingRunning) processingState = 'active';

    let labelGenState = null;
    if (isLabelGeneration) {
        if (isError && (isInLabelGenPhase || processingFinished)) {
            labelGenState = 'error';
        } else if (isError) {
            labelGenState = deferLabelGeneration ? 'waiting' : 'pending';
        } else if (file.isWarning) {
            labelGenState = 'warning';
        } else if (isInLabelGenPhase || (deferLabelGeneration && labelGenerationArmed && processingFinished)) {
            labelGenState = 'active';
        } else if (deferLabelGeneration) {
            labelGenState = 'waiting';
        } else if (processingFinished && (isCompleted || phase === 'completed')) {
            labelGenState = 'complete';
        } else {
            labelGenState = 'pending';
        }
    }

    const waitingCopy = labelGenState === 'waiting' ? waitingCopyFor(file, labelGenerationArmed) : null;

    const uploadPctDisplay =
        uploadProgress !== null
            ? Math.max(0, Math.min(100, Math.round(uploadProgress)))
            : uploadState === 'active' && overall > 0 && overall <= 50
                ? Math.max(0, Math.min(100, Math.round((overall / 50) * 100)))
                : 0;

    const processingPctDisplay =
        processingState === 'active'
            ? Math.max(
                0,
                Math.min(
                    100,
                    typeof procRaw === 'number' && !Number.isNaN(procRaw)
                        ? Math.round(processingProgress)
                        : overall >= 50 && overall <= 100
                            ? Math.round(((overall - 50) / 50) * 100)
                            : Math.round(processingProgress)
                )
            )
            : processingState === 'complete'
                ? 100
                : 0;

    return {
        understandState: null,
        understandPctDisplay: 0,
        uploadState,
        processingState,
        uploadPctDisplay,
        processingPctDisplay,
        labelGenState,
        labelGenPct: typeof labelGenerationProgress === 'number' ? labelGenerationProgress : 0,
        waitingCopy,
    };
}
